import { useState, useEffect } from "react";
import { styledComponent } from "../utils/styled";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Modal,
  Radio,
  Select,
  TableCell,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import { ChevronDown, Check, X } from "lucide-mui";
import { forwardRef } from "react";
import { getBannedHandCursor, CheckIcon } from "./icons";

// ── Buttons ───────────────────────────────────────────────────────────────────

export const OutlinedBtn = styledComponent(Button)(({ isDark }) => ({
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "12px",
  padding: "0 16px",
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "0.15%px",
  color: isDark ? "#ECEFF3" : "#434950",
  border: `1px solid ${isDark ? "#6A707980" : "#D5D9DF"}`,
  textTransform: "none",
  gap: "4px",
  minWidth: 0,
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  background: isDark
    ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
    : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
  "&:hover": {
    background: isDark
      ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
      : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
  },
  "& .MuiButton-startIcon": { margin: 0 },
}));

export const PrimaryBtn = styledComponent(Button)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  color: isDark ? "#D5D9DF" : "#F3F6FA",
  letterSpacing: "0.15%",
  background: isDark
    ? "linear-gradient(135deg, rgba(168, 174, 182, 0.5) 0%, rgba(146, 153, 161, 0.5) 50%, rgba(126, 132, 141, 0.5) 100%)"
    : "linear-gradient(135deg, rgba(101, 107, 115, 0.3) 0%, rgba(74, 79, 85, 0.4) 50%, rgba(48, 52, 59, 0.4) 100%)",
  "&:hover": {
    background: isDark
      ? "linear-gradient(135deg, rgba(168, 174, 182, 0.5) 0%, rgba(146, 153, 161, 0.5) 50%, rgba(126, 132, 141, 0.5) 100%)"
      : "linear-gradient(135deg, rgba(101, 107, 115, 0.3) 0%, rgba(74, 79, 85, 0.4) 50%, rgba(48, 52, 59, 0.4) 100%)",
  },
  borderRadius: "12px",
  height: "36px",
  padding: "0 15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textTransform: "none",
  border: "1px solid",
  borderColor: "#ECEFF366",
}));

export const CancelBtn = styledComponent(Button)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  color: isDark ? "#D5D9DF" : "#434950",
  background: isDark
    ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
    : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
  "&:hover": {
    background: isDark
      ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
      : "linear-gradient(135deg, rgba(248, 249, 250, 0.8) 0%, rgba(226, 231, 235, 0.9) 50%, #D3DAE1 100%)",
  },
  borderRadius: "12px",
  height: "36px",
  padding: "0 15px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textTransform: "none",
  border: "1px solid",
  borderColor: isDark ? "#6A707980" : "#CACBCD66",
}));

// ── Typography ────────────────────────────────────────────────────────────────

export const PageTitle = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "24px",
  lineHeight: "36px",
  color: isDark ? "#ECEFF3" : "#434950",
}));

const RequiredAsterisk = styledComponent("span")({
  color: "#C12E30",
});

export const FormFieldLabel = styledComponent(Typography)(({ isDark }) => ({
  display: "block",
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.15px",
  color: isDark ? "#D5D9DF" : "#6A7079",
  marginBottom: "7.5px",
}));

export const FieldError = styledComponent(Typography)({
  fontFamily: "Inter",
  fontWeight: 500,
  color: "#C12E30",
  fontSize: "14px",
  lineHeight: "20px",
  marginTop: "8px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

// ── AnimatedDots ──────────────────────────────────────────────────────────────


const stepAnimation = (name, activeAtPercent, from, to) => ({
  ...from,
  animation: `${name} 1200ms infinite`,
  [`@keyframes ${name}`]: {
    "0%": from,
    [`${activeAtPercent}%`]: from,
    [`${activeAtPercent + 0.01}%`]: to,
    "100%": to,
  },
});

export function AnimatedDots({
  sx,
  variant = "text",
  dotSize = 6,
  dotColor = "#34D399",
  dotGap = 5,
}) {
  if (variant === "text") {
    return (
      <Box component='span' aria-hidden sx={{ display: "inline-flex", ...sx }}>
        {[0, 1, 2].map(i => (
          <Box
            key={i}
            component='span'
            sx={stepAnimation(`animatedDotFade${i}`, (i + 1) * 25, { opacity: 0 }, { opacity: 1 })}
          >
            .
          </Box>
        ))}
      </Box>
    );
  }


  const inactive = { height: `${dotSize * (2.8 / 6)}px`, opacity: 0.35 };
  const active = { height: `${dotSize * (5.56 / 6)}px`, opacity: 0.76 };
  return (
    <Box
      component='span'
      aria-hidden
      sx={{ display: "inline-flex", alignItems: "center", gap: `${dotGap}px`, ...sx }}
    >
      {[0, 1, 2].map(i => (
        <Box
          key={i}
          component='span'
          sx={{
            width: `${dotSize}px`,
            height: `${dotSize}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component='span'
            sx={{
              width: `${dotSize}px`,
              borderRadius: "9999px",
              background: dotColor,
              // Dot 0 is always active; the rest activate in sequence.
              ...(i === 0 ? active : stepAnimation(`animatedDotGrow${i}`, (i * 100) / 3, inactive, active)),
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

// ── Dialog helpers ────────────────────────────────────────────────────────────

export const dialogPaperSx = isDark => ({
  width: "508px",
  borderRadius: "12px",
  border: isDark ? "1px solid #6A7079" : "1px solid #FFFFFF80",
  background: isDark
    ? "linear-gradient(172.84deg, rgba(49, 54, 59, 0.98) 2.34%, rgba(37, 40, 42, 0.95) 63.24%, rgba(30, 32, 34, 0.98) 124.13%)"
    : "linear-gradient(113.4deg, #F2F5FA 14.62%, #E9EBED 77.08%)",
  p: "25px",
});

export const dialogTitleSx = isDark => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: "20px",
  color: isDark ? "#FFFFFF" : "#434950",
  mb: "20px",
  p: 0,
});

// ── Form components ───────────────────────────────────────────────────────────

export function FormTextField({
  label,
  value,
  onChange,
  error,
  isDark,
  containerSx,
  required,
  ...props
}) {
  return (
    <Box sx={{ mb: "15px", ...containerSx }}>
      <FormFieldLabel component='label' isDark={isDark}>
        {label}
        {required && <RequiredAsterisk> *</RequiredAsterisk>}
      </FormFieldLabel>
      <TextField
        fullWidth
        value={value}
        onChange={onChange}
        required={required}
        autoComplete='off'
        variant='outlined'
        sx={{
          "& .MuiOutlinedInput-root": {
            height: "42px",
            background: isDark
              ? "linear-gradient(135deg, rgba(168, 174, 182, 0.2) 0%, rgba(126, 132, 141, 0.2) 100%)"
              : "#FFFFFF",
            borderRadius: "10px",
            color: isDark ? "#D5D9DF" : "#575D64",
            fontFamily: "Inter",
            fontSize: "14px",
            "& fieldset": { borderColor: isDark ? "#ECEFF333" : "#D5D9DF" },
            "&:hover fieldset": {
              borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
            },
            "&.Mui-focused fieldset": {
              borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              background: isDark
                ? "linear-gradient(135deg, rgba(168, 174, 182, 0.2) 0%, rgba(126, 132, 141, 0.2) 100%)"
                : "#FFFFFF",
              "& fieldset": { borderColor: isDark ? "#ECEFF333" : "#D5D9DF" },
            },
          },
          "& .MuiInputBase-input": {
            color: isDark ? "#D5D9DF" : "#575D64",
            padding: "13px 14px",
            height: "16px",
            boxSizing: "content-box",
            "&.Mui-disabled": {
              WebkitTextFillColor: isDark ? "#D5D9DF" : "#575D64",
            },
            "&:-webkit-autofill": {
              WebkitBoxShadow: isDark
                ? "0 0 0 100px #1a1a1a inset"
                : "0 0 0 100px #FFFFFF inset",
              WebkitTextFillColor: isDark ? "#DEDEDE" : "#434950",
              borderRadius: "4px",
              height: "16px",
              boxSizing: "content-box",
            },
            "&:-webkit-autofill:hover": {
              WebkitBoxShadow: isDark
                ? "0 0 0 100px #1a1a1a inset"
                : "0 0 0 100px #FFFFFF inset",
              WebkitTextFillColor: isDark ? "#DEDEDE" : "#434950",
            },
            "&:-webkit-autofill:focus": {
              WebkitBoxShadow: isDark
                ? "0 0 0 100px #1a1a1a inset"
                : "0 0 0 100px #FFFFFF inset",
              WebkitTextFillColor: isDark ? "#DEDEDE" : "#434950",
            },
          },
        }}
        {...props}
      />
      {error && (
        <FieldError>
          <InfoIcon sx={{ fontSize: "14px", color: "#C12E30" }} />
          {error}
        </FieldError>
      )}
    </Box>
  );
}

export function FormSelect({
  label,
  value,
  onChange,
  options,
  error,
  isDark,
  clearable,
  hideCheck,
  containerSx,
}) {
  return (
    <FormControl fullWidth sx={{ mb: "15px", ...containerSx }}>
      {label && (
        <FormFieldLabel component='label' isDark={isDark}>
          {label}
        </FormFieldLabel>
      )}
      <Select
        value={value}
        onChange={onChange}
        renderValue={selected =>
          selected || (
            <span
              style={{
                color: "#6A7079",
                fontFamily: "Inter",
                fontSize: "14px",
                fontStyle: "italic",
              }}
            >
              - Select -
            </span>
          )
        }
        variant='outlined'
        displayEmpty
        IconComponent={ChevronDown}
        sx={{
          height: "42px",
          background: isDark
            ? "linear-gradient(135deg, rgba(168, 174, 182, 0.2) 0%, rgba(126, 132, 141, 0.2) 100%)"
            : "#FFFFFF",
          borderRadius: "10px",
          color: isDark ? "#D5D9DF" : "#575D64",
          fontFamily: "Inter",
          fontSize: "14px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
            borderWidth: "1px",
          },
          "& .MuiSelect-select": {
            padding: "13px 14px",
            height: "16px",
            boxSizing: "content-box",
          },
          "& .MuiSvgIcon-root": {
            color: isDark ? "#8c8c8c" : "#6B7280",
            right: "8px",
            fontSize: "18px",
          },
        }}
        MenuProps={{
          MenuListProps: {
            disablePadding: true,
            sx: {
              maxHeight: "143px",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "#6A7079" : "rgba(0,0,0,0.2)",
                borderRadius: "6px",
              },
              "& .MuiMenuItem-root": {
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "20px",
                letterSpacing: "0.035px",
                color: isDark ? "#ECEFF3" : "#575D64",
                padding: "4px 7px",
                borderRadius: "8px",
                mb: "4px",
                mr: "4px",
                "&:last-child": { mb: 0 },
                "&.Mui-selected": {
                  bgcolor: isDark ? "#1E2022" : "#ECEFF3",
                  border: "1px solid",
                  borderColor: isDark ? "#1E2022" : "#D5D9DF",
                },
                "&.Mui-selected:hover": {
                  bgcolor: isDark ? "#1E2022" : "#ECEFF3",
                  border: "1px solid",
                  borderColor: isDark ? "#1E2022" : "#D5D9DF",
                },
                "&:hover": { bgcolor: isDark ? "#2D3032" : "#EFF1F3" },
              },
            },
          },
          PaperProps: {
            sx: {
              background: isDark
                ? "linear-gradient(129.13deg, #3B3D3F 9.02%, #2E3136 93.58%)"
                : "linear-gradient(129.13deg, #FAFCFF 9.02%, #FAFBFD 93.58%)",
              border: "1px solid",
              borderColor: isDark ? "#6A7079" : "#ECEFF399",
              color: isDark ? "#D5D9DF" : "#575D64",
              borderRadius: "12px",
              mt: "8px",
              overflow: "hidden",
              p: "4px",
            },
          },
        }}
      >
        {clearable && (
          <MenuItem
            value=''
            disableRipple
            sx={{
              display: "flex",
              alignItems: "center",
              borderRadius: "10px",
              fontStyle: "italic",
              color: isDark ? "#6A7079" : "#9CA3AF",
              "&:hover": { bgcolor: isDark ? "#2D3032" : "#EFF1F3" },
              "&&.Mui-selected, &&.Mui-selected:hover": {
                bgcolor: isDark ? "#1E2022" : "#ECEFF3",
                border: "1px solid",
                borderColor: isDark ? "#1E2022" : "#D5D9DF",
              },
              "&.Mui-focusVisible": { bgcolor: "transparent" },
            }}
          >
            - Select -
          </MenuItem>
        )}
        {options.map(opt => (
          <MenuItem
            disableRipple
            key={opt}
            value={opt}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: "10px",
              "&&.Mui-selected, &&.Mui-selected:hover": {
                bgcolor: isDark ? "#1E2022" : "#ECEFF3",
                border: "1px solid",
                borderColor: isDark ? "#1E2022" : "#D5D9DF",
              },
            }}
          >
            {opt}
            {!hideCheck && value === opt && <Check sx={{ fontSize: "16px" }} />}
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FieldError>
          <InfoIcon sx={{ fontSize: "15px", color: "#C12E30" }} />
          {error}
        </FieldError>
      )}
    </FormControl>
  );
}

export function FormTextArea({
  label,
  value,
  onChange,
  error,
  isDark,
  rows = 4,
  placeholder,
  containerSx,
  ...props
}) {
  return (
    <Box sx={{ mb: "15px", ...containerSx }}>
      <FormFieldLabel component='label' isDark={isDark}>
        {label}
      </FormFieldLabel>
      <TextField
        fullWidth
        multiline
        rows={rows}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        variant='outlined'
        sx={{
          "& .MuiOutlinedInput-root": {
            background: isDark
              ? "linear-gradient(135deg, rgba(168, 174, 182, 0.2) 0%, rgba(126, 132, 141, 0.2) 100%)"
              : "#FFFFFF",
            borderRadius: "10px",
            color: isDark ? "#D5D9DF" : "#575D64",
            fontFamily: "Inter",
            fontSize: "14px",
            "& fieldset": { borderColor: isDark ? "#ECEFF333" : "#D5D9DF" },
            "&:hover fieldset": {
              borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
            },
            "&.Mui-focused fieldset": {
              borderColor: isDark ? "#ECEFF333" : "#D5D9DF",
              borderWidth: "1px",
            },
          },
          "& .MuiInputBase-input": {
            color: isDark ? "#D5D9DF" : "#575D64",
            fontFamily: "Inter",
            fontSize: "14px",
            padding: "3px 4px",
            "&::placeholder": {
              color: isDark ? "#6A7079" : "#9CA3AF",
              opacity: 1,
            },
          },
        }}
        {...props}
      />
      {error && (
        <FieldError>
          <InfoIcon sx={{ fontSize: "14px", color: "#C12E30" }} />
          {error}
        </FieldError>
      )}
    </Box>
  );
}

// ── RadioField ────────────────────────────────────────────────────────────────

export function RadioField({ value, label, isDark, size = 18, sx }) {
  const ringSx = {
    width: size,
    height: size,
    borderRadius: "50%",
    border: "1px solid",
    bgcolor: isDark ? "#40464D" : "#FFFFFF",
    boxShadow: isDark
      ? "0px 2px 4px 0px #F3F6FA26 inset"
      : "0px 2px 4px 0px #16181A1F inset",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <FormControlLabel
      value={value}
      label={label ?? ""}
      control={
        <Radio
          disableRipple
          icon={
            <Box
              sx={{ ...ringSx, borderColor: isDark ? "#6A707980" : "#D5D9DF" }}
            />
          }
          checkedIcon={
            <Box
              sx={{ ...ringSx, borderColor: isDark ? "#F3F6FACC" : "#6A7079" }}
            >
              <Box
                sx={{
                  width: (12 * size) / 20,
                  height: (12 * size) / 20,
                  borderRadius: "50%",
                  bgcolor: isDark ? "#F3F6FA" : "#6A7079",
                }}
              />
            </Box>
          }
          sx={{ p: 0, "&:hover": { bgcolor: "transparent" } }}
        />
      }
      sx={{
        m: 0,
        gap: "8px",
        alignItems: "center",
        "& .MuiFormControlLabel-label": {
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "16px",
          lineHeight: "20px",
          color: isDark ? "#DEDEDE" : "#434950",
        },
        ...sx,
      }}
    />
  );
}

// ── CheckboxField ─────────────────────────────────────────────────────────────

export function CheckboxField({
  isDark,
  checked,
  onChange,
  disabled,
  size = 20,
  sx,
  disabledCustomIcon,
  ...props
}) {
  const showDisabledCustomIcon = Boolean(disabledCustomIcon) && disabled;

  return (
    <Checkbox
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      disableRipple
      icon={
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: "6px",
            border: "1px solid",
            borderColor: isDark ? "#6A707980" : "#D5D9DF",
            bgcolor: isDark ? "#40464D" : "#FFFFFF",
            boxShadow: isDark
              ? "0px 2px 4px 0px #F3F6FA26 inset"
              : "0px 2px 4px 0px #16181A1F inset",
            boxSizing: "border-box",
          }}
        />
      }
      checkedIcon={
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: "6px",
            border: "1px solid ",
            borderColor: isDark ? "#B8C0CB" : "#6A707980",
            bgcolor: isDark ? "#B8C0CB" : "#434950E5",
            boxShadow: isDark ? "none" : "0px 2px 4px 0px #F3F6FA26 inset",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CheckIcon
            width={(10 * size) / 20}
            height={(8 * size) / 20}
            color={isDark ? "#262A2E" : "#F3F6FA"}
          />
        </Box>
      }
      sx={{
        padding: 0,
        flexShrink: 0,
        ...(showDisabledCustomIcon && {
          "&.Mui-disabled": {
            cursor: getBannedHandCursor(isDark ? "#FFFFFF" : "#231F20"),
            pointerEvents: "auto",
          },
        }),
        ...sx,
      }}
      {...props}
    />
  );
}

// ── Table components ──────────────────────────────────────────────────────────

export const TableWrapper = styledComponent(Box)(({ isDark, fullHeight }) => ({
  flex: 1,
  minWidth: 0,
  borderRadius: "20px",
  backgroundColor: isDark ? "#1C1D1E" : "#FFFFFF",
  padding: "20px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  ...(fullHeight && { height: "100%" }),
}));

export const TableCard = styledComponent(TableContainer)({
  flex: 1,
  overflow: "auto",
  background: "transparent",
  boxShadow: "none",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

// ── Edit section table (Tools / Parts editors) ─────────────────────────────────

export const EditSectionRoot = styledComponent(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

export const EditTableCard = styledComponent(Box)(({ isDark }) => ({
  marginTop: "16px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  borderRadius: "16px",
  border: "1px solid",
  borderColor: isDark ? "#262A2E" : "#F1F4F8",
  background: isDark ? "#25282A99" : "#FFFFFF",
  boxShadow: isDark ? "0px 4px 4px 0px #00000040" : "none",
  overflow: "hidden",
}));

export const EditTableHeaderRow = styledComponent(Box)(
  ({ isDark, columns }) => ({
    display: "grid",
    gridTemplateColumns: columns,
    gap: "12px",
    padding: "0 30px",
    background: isDark ? "#262A2E" : "#ECEFF3",
    borderBottom: "1px solid",
    borderColor: isDark ? "#262A2E" : "#F6F6F6",
    flexShrink: 0,
  }),
);

export const EditTableHeaderLabel = styledComponent(Typography)(
  ({ isDark }) => ({
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.021px",
    color: isDark ? "#ECEFF3" : "#434950",
    padding: "16px 8px",
  }),
);

export const EditTableDataRows = styledComponent(Box)({
  flex: 1,
  overflowY: "auto",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
});

export const EditTableRow = styledComponent(Box)(({ isDark, columns }) => ({
  display: "grid",
  gridTemplateColumns: columns,
  alignItems: "center",
  gap: "12px",
  height: "48px",
  padding: "0 30px",
  background: isDark ? "#191A1B66" : "transparent",
  borderBottom: "1px solid",
  borderColor: isDark ? "#262A2E" : "#F6F6F6",
}));

export const AddEntityButtonWrap = styledComponent(Box)({
  marginTop: "20px",
  flexShrink: 0,
});

export const AddEntityButton = styledComponent(Box)(({ isDark }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "8px 16px",
  borderRadius: "12px",
  cursor: "pointer",
  border: "1px solid",
  borderColor: isDark ? "rgba(106,112,121,0.5)" : "#D5D9DF",
  background: isDark
    ? "linear-gradient(162.6deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
    : "linear-gradient(162.3deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
}));

export const AddEntityButtonLabel = styledComponent(Typography)(
  ({ isDark }) => ({
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.021px",
    color: isDark ? "#ECEFF3" : "#434950",
  }),
);

export const HeadCell = styledComponent(TableCell)(({ isDark, bold }) => ({
  fontFamily: "Inter",
  fontWeight: bold ? 700 : 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25%",
  color: isDark ? "#ECEFF3" : "#434950",
  backgroundColor: isDark ? "#262A2E" : "#F3F6FA",
  borderBottom: "none",
  whiteSpace: "nowrap",
  height: "48px",
  padding: "0 18px",
  "&:first-of-type": {
    borderTopLeftRadius: "12px",
    borderBottomLeftRadius: "12px",
  },
  "&:last-of-type": {
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
  },
}));

export const BodyCell = styledComponent(TableCell)(
  ({ isDark, truncate, isLast }) => ({
    fontFamily: "Inter",
    fontSize: "14px",
    fontWeight: 400,
    color: isDark ? "#ECEFF3" : "#434950",
    backgroundColor: "inherit",
    borderBottom: isLast
      ? "none"
      : `1px solid ${isDark ? "#262A2E" : "#F6F6F6"}`,
    height: "36px",
    padding: "0 18px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    ...(truncate && {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: 0,
      "&:last-of-type": {
        maxWidth: "80px",
        width: "80px",
        minWidth: "80px",
      },
    }),
  }),
);

// ── Generic workflow status pill (Logbook/Submissions "Complete", "Pending", ─
// "Approved", etc.) — fully color/icon-driven, no built-in status knowledge.
// Props are prefixed `pill*`, not the generic `size`/`gap`/etc., since the
// shared styled-component prop filter is global (see utils/styled.js).

const WorkflowStatusPillRoot = styledComponent(Box)(
  ({ isDark, pillSize, bg, border, pillGap, pillPadding }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: pillGap ?? (pillSize === "lg" ? (isDark ? "6px" : "2px") : "2px"),
    padding:
      pillPadding ??
      (pillSize === "lg" ? (isDark ? "6px 12px" : "2px 8px") : "2px 8px"),
    borderRadius: "100px",
    backgroundColor: bg,
    border: `1px solid ${border}`,
    overflow: "hidden",
    whiteSpace: "nowrap",
    flexShrink: 0,
    width: pillSize === "lg" ? "auto" : "fit-content",
  }),
);

const WorkflowStatusPillLabel = styledComponent(Typography)(
  ({ isDark, pillSize, color, pillFontSize, pillLetterSpacing }) => ({
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize:
      pillFontSize ?? (pillSize === "lg" ? (isDark ? "14px" : "12px") : "12px"),
    letterSpacing:
      pillLetterSpacing ??
      (pillSize === "lg" ? (isDark ? "0.021px" : "0.03px") : "0.03px"),
    lineHeight: "20px",
    whiteSpace: "nowrap",
    color,
  }),
);

// `pillGap`/`pillPadding`/`pillFontSize`/`pillLetterSpacing`/`iconSize` all
// optionally override the size-based defaults for one-off pills.
export function WorkflowStatusPill({
  icon,
  iconSize,
  label,
  color,
  bg,
  border,
  pillSize = "sm",
  isDark,
  pillGap,
  pillPadding,
  pillFontSize,
  pillLetterSpacing,
}) {
  const Icon = icon;
  const resolvedIconSize =
    iconSize ?? (pillSize === "lg" ? (isDark ? "16px" : "13px") : "13px");
  return (
    <WorkflowStatusPillRoot
      isDark={isDark}
      pillSize={pillSize}
      bg={bg}
      border={border}
      pillGap={pillGap}
      pillPadding={pillPadding}
    >
      <Icon sx={{ fontSize: resolvedIconSize, color }} />
      <WorkflowStatusPillLabel
        isDark={isDark}
        pillSize={pillSize}
        color={color}
        pillFontSize={pillFontSize}
        pillLetterSpacing={pillLetterSpacing}
      >
        {label}
      </WorkflowStatusPillLabel>
    </WorkflowStatusPillRoot>
  );
}

// ── Theme / toggle pill ───────────────────────────────────────────────────────

export const TogglePill = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  background: isDark
    ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
    : "linear-gradient(135deg, rgba(190, 195, 202, 0.8) 0%, rgba(243, 246, 250, 0.8) 50%, rgba(236, 239, 243, 0.8) 100%)",
  borderRadius: "12px",
  padding: "4px",
  border: "1px solid",
  borderColor: isDark ? "#6A707980" : "#A8AEB680",
}));

export const ToggleOption = styledComponent(Box)(({ isDark, active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "26px",
  height: "26px",
  cursor: active ? "default" : "pointer",
  backgroundColor: active ? (isDark ? "#191A1B" : "#FDFEFE") : "transparent",
  transition: "background-color 0.2s",
  color: isDark ? "#F1F4F8" : active ? "#31363B" : "#A8AEB6",
  borderRadius: "8px",
  "& svg": { width: "16px", height: "16px" },
}));

// ── File context menu ─────────────────────────────────────────────────────────

const fileMenuPaperSx = isDark => ({
  bgcolor: isDark ? "#262A2E" : "#ECEFF3",
  p: "5px",
  pb: 0,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: isDark ? "#434950" : "#D5D9DF",
  minWidth: "90px",
  "& .MuiMenuItem-root": {
    fontFamily: "Inter",
    fontWeight: 400,
    fontSize: "14px",
    p: "6px 8px",
    mb: "5px",
    borderRadius: "8px",
    color: isDark ? "#D5D9DF" : "#434950",
    "&:hover": { bgcolor: isDark ? "#31363B" : "#DFE3E9" },
    "& .MuiTouchRipple-root": { display: "none" },
  },
});

// ── Page layout / pagination (shared by admin-style pages) ───────────────────

export const ContentArea = styledComponent(Box)(({ isDark }) => ({
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  backgroundColor: "transparent",
  padding: "24px",
  boxSizing: "border-box",
}));

export const PaginationRow = styledComponent(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "26px",
});

export const PaginationSide = styledComponent(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

export const PaginationLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.25%",
  color: isDark ? "#A8AEB6" : "#6A7079",
}));

export const PageNavBtn = styledComponent(IconButton)(({ isDark }) => ({
  height: "32px",
  width: "36px",
  padding: 0,
  boxSizing: "border-box",
  color: isDark ? "#D5D9DF" : "#6A7079",
  border: `1px solid ${isDark ? "#31363B" : "#A8AEB6"}`,
  borderRadius: "8px",
  backgroundColor: isDark ? "#262A2E" : "#D5D9DF",
  "& svg": { width: "16px", height: "16px" },
  "&.Mui-disabled": {
    color: isDark ? "#D5D9DF" : "#6A7079",
    backgroundColor: isDark ? "#262A2E" : "#D5D9DF",
  },
}));

export const PageSizeSelect = styledComponent(Select)(({ isDark }) => ({
  height: "32px",
  width: "70px",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 500,
  color: isDark ? "#A8AEB6" : "#6A7079",
  backgroundColor: isDark ? "#262A2E" : "#D5D9DF",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: isDark ? "#31363B" : "#A8AEB680",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: isDark ? "#31363B" : "#A8AEB680",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: isDark ? "#31363B" : "#A8AEB680",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
    color: isDark ? "#D5D9DF" : "#6A7079",
    width: "16px",
    height: "16px",
  },
}));

export function FileContextMenu({
  anchorEl,
  anchorPosition,
  open,
  onClose,
  isDark,
  children,
}) {
  const positionProps = anchorPosition
    ? { anchorReference: "anchorPosition", anchorPosition }
    : {
        anchorEl,
        transformOrigin: { horizontal: "left", vertical: "top" },
        anchorOrigin: { horizontal: "left", vertical: "bottom" },
      };

  return (
    <Menu
      open={open}
      onClose={onClose}
      {...positionProps}
      MenuListProps={{ disablePadding: true }}
      slotProps={{ paper: { elevation: 3, sx: fileMenuPaperSx(isDark) } }}
    >
      {children}
    </Menu>
  );
}

// ── Picker modal shell ────────────────────────────────────────────────────────

export const PickerModalShell = styledComponent(Box)(({ isDark }) => ({
  ...(isDark
    ? {
        backgroundImage:
          "linear-gradient(171.99deg, rgb(49,54,59) 2.34%, rgb(37,40,42) 63.24%, rgb(30,32,34) 124.13%)",
        border: "1px solid #6a7079",
      }
    : {
        backgroundColor: "#FFFFFF",
        border: "1px solid #E5E7EB",
      }),
  borderRadius: "12px",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  boxShadow: isDark
    ? "0px 16px 48px 0px rgba(0,0,0,0.5)"
    : "0px 8px 32px 0px rgba(0,0,0,0.12)",
  outline: "none",
}));

export const PickerModalHeader = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 32px",
  height: "56px",
  flexShrink: 0,
}));

export const PickerModalTitle = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 700,
  fontSize: "20px",
  lineHeight: "28px",
  letterSpacing: "0.1px",
  color: isDark ? "#FFFFFF" : "#31363b",
}));

export const PickerModalCloseBtn = styledComponent(Box)(({ isDark }) => ({
  width: 24,
  height: 24,
  backgroundColor: isDark ? "transparent" : "rgba(0,0,0,0.06)",
  borderRadius: "5px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

export const PickerSearchRow = styledComponent(Box)(({ isDark }) => ({
  padding: "12px 32px",
  height: "56px",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
}));

export const PickerSearchBox = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  background: isDark
    ? "linear-gradient(175.52deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
    : "rgba(0,0,0,0.04)",
  border: `1px solid ${isDark ? "rgba(106,112,121,0.5)" : "#d5d9df"}`,
  borderRadius: "12px",
  padding: "0 12px",
  height: "32px",
  flex: 1,
}));

export const PickerSearchInput = styledComponent(InputBase)(({ isDark }) => ({
  flex: 1,
  fontFamily: "Inter",
  fontSize: "14px",
  "& input": {
    color: isDark ? "#eceff3" : "#31363b",
    padding: 0,
    "&::placeholder": {
      color: isDark ? "#a8aeb6" : "rgba(49,54,59,0.4)",
      opacity: 1,
      fontStyle: "italic",
    },
  },
}));

export const PickerModalBody = styledComponent(Box)(({ isDark }) => ({
  flex: 1,
  overflowY: "auto",
  padding: "4px 26px 4px 27px",
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.15)",
    borderRadius: "99px",
  },
}));

export const PickerModalFooter = styledComponent(Box)(({ isDark }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "0 32px",
  height: "66px",
  borderTop: `1px solid ${isDark ? "rgba(106,112,121,0.5)" : "#E5E7EB"}`,
  flexShrink: 0,
  backgroundColor: isDark ? "#1e2124" : "#F5F6F8",
}));

export const PickerSelectedCount = styledComponent(Typography)(
  ({ isDark }) => ({
    fontFamily: "Inter",
    fontSize: "11px",
    color: isDark ? "#9a9c9e" : "#6B7280",
  }),
);

export const PickerFooterBtns = styledComponent(Box)({
  display: "flex",
  gap: "16px",
});

export const PickerCancelBtn = styledComponent(Box)(({ isDark }) => ({
  height: "36px",
  padding: "0 20px",
  display: "flex",
  alignItems: "center",
  ...(isDark && {
    backgroundImage:
      "linear-gradient(157.5deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)",
  }),
  border: `1px solid ${isDark ? "rgba(106,112,121,0.5)" : "#d5d9df"}`,
  borderRadius: "12px",
}));

export const PickerActionBtn = styledComponent(Box)({
  height: "36px",
  padding: "0 20px",
  display: "flex",
  alignItems: "center",
  background:
    "linear-gradient(154.61deg, rgba(168,174,182,0.5) 0%, rgba(146,153,161,0.5) 50%, rgba(126,132,141,0.5) 100%)",
  border: "1px solid rgba(236,239,243,0.4)",
  borderRadius: "12px",
});

export const PickerBtnLabel = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  lineHeight: "20px",
  letterSpacing: "0.021px",
  color: isDark ? "#d5d9df" : "#434950",
}));

// ── ConfirmDialog ─────────────────────────────────────────────────────────────

export const ConfirmDialogBox = styledComponent(Box)(({ isDark }) => ({
  backgroundImage: isDark
    ? "linear-gradient(169.73deg, rgba(49,54,59,0.98) 6.19%, rgba(30,32,34,0.98) 91.81%)"
    : "linear-gradient(168.6deg, rgb(242,245,250) 8.49%, rgb(233,235,237) 91.51%)",
  border: "1px solid rgba(106,112,121,0.5)",
  borderRadius: "12px",
  padding: "32px",
  width: "456px",
  display: "flex",
  flexDirection: "column",
  gap: "32px",
  alignItems: "center",
  outline: "none",
}));

export const ConfirmDialogTextGroup = styledComponent(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  alignItems: "center",
  width: "100%",
});

export const ConfirmDialogTitle = styledComponent(Typography)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 600,
  fontSize: "20px",
  lineHeight: "28px",
  color: isDark ? "#FFFFFF" : "#434950",
  letterSpacing: "-0.45px",
  textAlign: "center",
}));

export const ConfirmDialogMessage = styledComponent(Typography)(
  ({ isDark }) => ({
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.021px",
    color: isDark ? "#f4f4fb" : "#434950",
    textAlign: "center",
  }),
);

export const ConfirmDialogButtonRow = styledComponent(Box)({
  display: "flex",
  gap: "16px",
  justifyContent: "center",
});

export const ConfirmDialogCancelBtn = styledComponent(Box)(({ isDark }) => ({
  borderRadius: "12px",
  padding: "8px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: isDark
    ? "none"
    : "linear-gradient(154.94deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
  border: `1px solid ${isDark ? "rgba(106,112,121,0.5)" : "rgba(202,203,205,0.4)"}`,
}));

export const ConfirmDialogConfirmBtn = styledComponent(Box)(({ isDark }) => ({
  borderRadius: "12px",
  padding: "8px 20px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: isDark
    ? "linear-gradient(151deg, rgba(168,174,182,0.5) 0%, rgba(146,153,161,0.5) 50%, rgba(126,132,141,0.5) 100%)"
    : "linear-gradient(151deg, rgba(101,107,115,0.3) 0%, rgba(74,79,85,0.4) 50%, rgba(48,52,59,0.4) 100%)",
  border: "1px solid rgba(236,239,243,0.4)",
}));

export const ConfirmDialogBtnText = styledComponent(Typography)(
  ({ isDark }) => ({
    fontFamily: "Inter",
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.021px",
    color: isDark ? "#eceff3" : "#434950",
  }),
);

// ── Edit save / cancel buttons ────────────────────────────────────────────────

export const SaveBtn = styledComponent(Button)({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "0.021px",
  color: "#FFFFFF",
  background:
    "linear-gradient(151.75deg, rgb(20,110,55) 0%, rgb(26,135,68) 100%)",
  borderRadius: "12px",
  textTransform: "none",
  height: "36px",
  padding: "0 15px",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&.Mui-disabled": { opacity: 0.6, color: "#FFFFFF" },
});

export const CancelChangesBtn = styledComponent(Button)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "0.021px",
  color: isDark ? "#FFFFFF" : "#434950",
  background: isDark
    ? "linear-gradient(155.5deg, rgba(168,174,182,0.5) 0%, rgba(126,132,141,0.5) 100%)"
    : "linear-gradient(156deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
  border: "1px solid",
  borderColor: isDark ? "rgba(168,174,182,0.5)" : "#D5D9DF",
  borderRadius: "12px",
  textTransform: "none",
  height: "36px",
  padding: "0 16px",
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  "&.Mui-disabled": {
    color: isDark ? "#FFFFFF" : "#434950",
    background: isDark
      ? "linear-gradient(155.5deg, rgba(168,174,182,0.5) 0%, rgba(126,132,141,0.5) 100%)"
      : "linear-gradient(156deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
    borderColor: isDark ? "rgba(168,174,182,0.5)" : "#D5D9DF",
  },
}));

// Distinct gradient/border from CancelChangesBtn — used by the Procedure
// Submitted and Confirm Approval popups only.
export const NeutralGradientBtn = styledComponent(Button)(({ isDark }) => ({
  fontFamily: "Inter",
  fontWeight: 500,
  fontSize: "14px",
  letterSpacing: "0.021px",
  color: isDark ? "#ECEFF3" : "#434950",
  background: isDark
    ? "linear-gradient(155.5deg, rgba(67,73,80,0.8) 0%, rgba(49,54,59,0.8) 50%, rgba(49,54,59,0.8) 100%)"
    : "linear-gradient(155.5deg, rgba(248,249,250,0.8) 0%, rgba(226,231,235,0.9) 50%, rgb(211,218,225) 100%)",
  border: "1px solid",
  borderColor: isDark ? "rgba(106,112,121,0.5)" : "rgba(202,203,205,0.4)",
  borderRadius: "12px",
  textTransform: "none",
  height: "36px",
  padding: "0 20px",
  minWidth: 0,
}));

export function ConfirmDialog({
  open,
  onCancel,
  onConfirm,
  isLoading = false,
  title,
  message,
  confirmLabel = "Delete",
  danger = false,
  isDark = true,
  children,
}) {
  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : onCancel}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <ConfirmDialogBox isDark={isDark}>
        <ConfirmDialogTextGroup>
          <ConfirmDialogTitle isDark={isDark}>{title}</ConfirmDialogTitle>
          {children}
          <ConfirmDialogMessage isDark={isDark}>{message}</ConfirmDialogMessage>
        </ConfirmDialogTextGroup>
        <ConfirmDialogButtonRow>
          <ConfirmDialogCancelBtn
            isDark={isDark}
            onClick={isLoading ? undefined : onCancel}
            sx={{ cursor: isLoading ? "default" : "pointer" }}
          >
            <ConfirmDialogBtnText isDark={isDark}>Cancel</ConfirmDialogBtnText>
          </ConfirmDialogCancelBtn>
          <ConfirmDialogConfirmBtn
            isDark={isDark}
            onClick={isLoading ? undefined : onConfirm}
            sx={{
              cursor: isLoading ? "default" : "pointer",
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <ConfirmDialogBtnText isDark={isDark} sx={{ color: "#FFFFFF" }}>
                {confirmLabel}
              </ConfirmDialogBtnText>
            )}
          </ConfirmDialogConfirmBtn>
        </ConfirmDialogButtonRow>
      </ConfirmDialogBox>
    </Modal>
  );
}

// ── TypeToConfirmDialog ───────────────────────────────────────────────────────

// Confirm stays disabled until the typed text exactly matches requiredText.
export function TypeToConfirmDialog({
  open,
  onCancel,
  onConfirm,
  isLoading = false,
  title = "Confirmation",
  description,
  changes = [],
  requiredText = "CONFIRM",
  isDark = true,
}) {
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    if (open) setTypedText("");
  }, [open]);

  const canConfirm = typedText === requiredText;

  return (
    <Modal
      open={open}
      onClose={isLoading ? undefined : onCancel}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <ConfirmDialogBox isDark={isDark}>
        <ConfirmDialogTextGroup>
          <ConfirmDialogTitle isDark={isDark}>{title}</ConfirmDialogTitle>
          <ConfirmDialogMessage isDark={isDark}>
            {description}
          </ConfirmDialogMessage>
          {changes.length > 0 && (
            <Box
              component='ul'
              sx={{ m: 0, pl: "21px", listStyleType: "disc" }}
            >
              {changes.map(change => (
                <ConfirmDialogMessage
                  key={change}
                  component='li'
                  isDark={isDark}
                  sx={{ textAlign: "left" }}
                >
                  {change}
                </ConfirmDialogMessage>
              ))}
            </Box>
          )}
          <TextField
            fullWidth
            value={typedText}
            onChange={e => setTypedText(e.target.value)}
            disabled={isLoading}
            placeholder={requiredText}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "41px",
                borderRadius: "12px",
                background: isDark
                  ? "linear-gradient(135deg, rgba(168,174,182,0.2) 0%, rgba(126,132,141,0.2) 100%)"
                  : "#FFFFFF",
                color: isDark ? "#D5D9DF" : "#575D64",
                fontFamily: "Inter",
                fontSize: "14px",
                "& fieldset": { borderColor: isDark ? "#ECEFF333" : "#ECEFF3" },
                "&:hover fieldset": {
                  borderColor: isDark ? "#ECEFF333" : "#ECEFF3",
                },
                "&.Mui-focused fieldset": {
                  borderColor: isDark ? "#ECEFF333" : "#ECEFF3",
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": { padding: "0 16px" },
            }}
          />
        </ConfirmDialogTextGroup>
        <ConfirmDialogButtonRow>
          <ConfirmDialogCancelBtn
            isDark={isDark}
            onClick={isLoading ? undefined : onCancel}
            sx={{ cursor: isLoading ? "default" : "pointer" }}
          >
            <ConfirmDialogBtnText isDark={isDark}>Cancel</ConfirmDialogBtnText>
          </ConfirmDialogCancelBtn>
          <ConfirmDialogConfirmBtn
            isDark={isDark}
            onClick={canConfirm && !isLoading ? onConfirm : undefined}
            sx={{
              cursor: canConfirm && !isLoading ? "pointer" : "default",
              opacity: canConfirm ? 1 : 0.6,
            }}
          >
            {isLoading ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <ConfirmDialogBtnText isDark={isDark} sx={{ color: "#FFFFFF" }}>
                Confirm
              </ConfirmDialogBtnText>
            )}
          </ConfirmDialogConfirmBtn>
        </ConfirmDialogButtonRow>
      </ConfirmDialogBox>
    </Modal>
  );
}
// ── ContextMenuItem ───────────────────────────────────────────────────────────

export function ContextMenuItem({ isDark, label, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        px: "8px",
        py: "6px",
        mb: "5px",
        borderRadius: "8px",
        cursor: "pointer",
        "&:hover": { bgcolor: isDark ? "#31363B" : "#DFE3E9" },
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter",
          fontWeight: 400,
          fontSize: "14px",
          lineHeight: "20px",
          color: isDark ? "#D5D9DF" : "#434950",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ── ContextMenu ───────────────────────────────────────────────────────────────

export const ContextMenu = forwardRef(function ContextMenu(
  { isDark, items },
  ref,
) {
  return (
    <Box
      ref={ref}
      sx={{
        position: "absolute",
        right: "2px",
        top: "calc(100% + 2px)",
        zIndex: 200,
        minWidth: "86px",
        borderRadius: "8px",
        bgcolor: isDark ? "#262A2E" : "#ECEFF3",
        border: "1px solid",
        borderColor: isDark ? "#434950" : "#D5D9DF",
        boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
        overflow: "hidden",
        pt: "5px",
        px: "5px",
        pb: 0,
      }}
    >
      {items.map(({ label, onClick }) => (
        <ContextMenuItem
          key={label}
          isDark={isDark}
          label={label}
          onClick={onClick}
        />
      ))}
    </Box>
  );
});
